import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  Clipboard,
  useWindowDimensions,
} from 'react-native';
import Icon from '@expo/vector-icons/MaterialIcons';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { fonts } from '../utils/fonts';

// Superadmin user-detail view. Firestore fields come from the `user` prop;
// Firebase Auth data (sign-in providers, sign-in times) and activity rolled up
// from other collections come from the getUserAdminDetails callable.

type AdminDetails = {
  auth: {
    providers: { providerId: string; email: string | null; displayName: string | null }[];
    emailVerified: boolean;
    disabled: boolean;
    phoneNumber: string | null;
    creationTime: string | null;
    lastSignInTime: string | null;
    lastRefreshTime: string | null;
  } | null;
  stats: {
    applications: number;
    applicationsByStatus: Record<string, number>;
    notifications: number;
    unreadNotifications: number;
    supportTickets: number;
  };
  recentApplications: {
    id: string;
    jobTitle: string | null;
    companyName: string | null;
    status: string;
    appliedAt: string | null;
  }[];
};

type TabKey = 'overview' | 'profile' | 'company' | 'activity' | 'settings';

const PROVIDERS: Record<string, { label: string; icon: string; color: string }> = {
  'google.com': { label: 'Google', icon: 'g-translate', color: '#EA4335' },
  password: { label: 'Email/Password', icon: 'email', color: '#6366F1' },
  phone: { label: 'Phone', icon: 'phone', color: '#10B981' },
  'apple.com': { label: 'Apple', icon: 'phone-iphone', color: '#64748B' },
  'facebook.com': { label: 'Facebook', icon: 'facebook', color: '#1877F2' },
};

const ROLE_COLORS: Record<string, string> = {
  superadmin: '#EF4444',
  admin: '#F59E0B',
  company: '#3B82F6',
  user: '#10B981',
};

const PLAN_COLORS: Record<string, string> = {
  enterprise: '#6366F1',
  pro: '#8B5CF6',
  standard: '#F59E0B',
  free: '#64748B',
};

const APPLICATION_STATUS_COLORS: Record<string, string> = {
  pending: '#F59E0B',
  reviewed: '#3B82F6',
  shortlisted: '#8B5CF6',
  accepted: '#10B981',
  rejected: '#EF4444',
};

// Firestore Timestamp, {seconds}, ISO string, or Date -> Date
const toDate = (value: any): Date | null => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (value: any, withTime = false) => {
  const d = toDate(value);
  if (!d) return '—';
  return withTime
    ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : d.toLocaleDateString(undefined, { dateStyle: 'medium' });
};

const timeAgo = (value: any) => {
  const d = toDate(value);
  if (!d) return '';
  const seconds = Math.round((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const units: [number, string][] = [
    [60 * 60 * 24 * 365, 'year'],
    [60 * 60 * 24 * 30, 'month'],
    [60 * 60 * 24, 'day'],
    [60 * 60, 'hour'],
    [60, 'minute'],
  ];
  for (const [size, name] of units) {
    if (seconds >= size) {
      const n = Math.floor(seconds / size);
      return `${n} ${name}${n === 1 ? '' : 's'} ago`;
    }
  }
  return '';
};

const ageFrom = (dob: any) => {
  const d = toDate(dob);
  if (!d) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) age--;
  return age >= 0 && age < 130 ? age : null;
};

// First photo that a browser can actually load. Some older signups stored the
// ImagePicker's on-device path (file:///data/user/0/...) instead of an
// uploaded URL, so skip anything that isn't http(s) or an inline data URI.
export const pickAvatarUrl = (user: any): string | null => {
  const candidates = [user?.profilePicture, user?.photoURL, user?.profile?.avatar];
  return candidates.find((u) => typeof u === 'string' && /^(https?:|data:image\/)/.test(u)) || null;
};

const titleCase =(s: string) => s.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

const nonEmpty = (arr: any): string[] =>
  Array.isArray(arr) ? arr.filter((v) => typeof v === 'string' && v.trim()) : [];

export default function UserDetailModal({
  user,
  colors,
  isDarkMode,
  onClose,
}: {
  user: any;
  colors: any;
  isDarkMode: boolean;
  onClose: () => void;
}) {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;
  const [tab, setTab] = useState<TabKey>('overview');
  const [details, setDetails] = useState<AdminDetails | null>(null);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setDetails(null);
    setDetailsError(null);
    httpsCallable(functions, 'getUserAdminDetails')({ uid: user.id })
      .then((res: any) => !cancelled && setDetails(res.data))
      .catch((err) => {
        console.error('❌ Error fetching user admin details:', err);
        if (!cancelled) setDetailsError(err?.message || 'Could not load account data');
      });
    return () => {
      cancelled = true;
    };
  }, [user.id]);

  const [avatarFailed, setAvatarFailed] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);

  // Escape closes the photo viewer first, then the whole panel (web only)
  useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (photoOpen) setPhotoOpen(false);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [photoOpen, onClose]);

  // ── Derived data (field names have changed over the app's life; read old and new) ──
  const name = user.displayName || user.name || 'No Name';
  const avatar = avatarFailed ? null : pickAvatarUrl(user);
  // Google avatar URLs end in a size directive like "=s96-c" (96px, cropped);
  // "=s0" asks for the original upload.
  const fullAvatar = avatar?.replace(/=s\d+(-c)?$/, '=s0') || null;
  const role = user.role || 'user';
  const plan = user.subscriptionId || 'free';
  const phone = user.phoneNumber || user.profile?.phone || details?.auth?.phoneNumber;
  const bio = user.profile?.bio || user.bio;
  const age = ageFrom(user.dateOfBirth);

  const interestedCategories: string[] = nonEmpty(
    user.interestedCategories?.length
      ? user.interestedCategories
      : user.interestedCategory
      ? [user.interestedCategory]
      : user.selectedCategories,
  );
  const interestedSubcategories = nonEmpty(user.interestedSubcategories);
  const interestedTags = nonEmpty(user.interestedTags?.length ? user.interestedTags : user.selectedTags);
  const preferredCategories = nonEmpty(user.preferredCategories);
  const preferredLocations = nonEmpty(user.preferredLocations);

  const skills = nonEmpty(user.skills?.length ? user.skills : user.skillTags);
  const skillCategories = nonEmpty(user.skillCategories);
  const skillSubcategories = nonEmpty(user.skillSubcategories);
  const education =
    typeof user.education === 'string'
      ? user.education
      : user.education
      ? [user.education.degree, user.education.institution, user.education.year].filter(Boolean).join(' · ')
      : '';
  const yearsOfExperience = user.yearsOfExperience ?? user.experience;
  const jobTitle = user.jobTitle || user.portfolio?.jobTitle;
  const portfolioLinks = [
    ['CV', user.portfolio?.cvUrl],
    ['Portfolio', user.portfolio?.portfolioUrl],
    ['LinkedIn', user.portfolio?.linkedin],
    ['GitHub', user.portfolio?.github],
  ].filter(([, url]) => url) as [string, string][];
  const socialLinks = Object.entries(user.socialLinks || {}).filter(([, url]) => url) as [string, string][];

  const companyProfile = user.companyProfile || {};
  const isCompany = role === 'company' && !!user.companyProfile;
  const company = {
    description: companyProfile.description || companyProfile.companyDescription,
    size: companyProfile.size || companyProfile.companySize,
    email: companyProfile.email || companyProfile.companyEmail,
    phone: companyProfile.phone || companyProfile.companyPhone,
    links: [
      ['Website', companyProfile.website],
      ['LinkedIn', companyProfile.socialMedia?.linkedIn || companyProfile.linkedIn],
      ['Facebook', companyProfile.socialMedia?.facebook || companyProfile.facebook],
      ['Twitter', companyProfile.socialMedia?.twitter || companyProfile.twitter],
      ['Logo', companyProfile.logoUrl],
    ].filter(([, url]) => url) as [string, string][],
  };

  const savedJobsCount = (user.savedJobs || user.bookmarkedJobs || []).length;
  const devices: string[] = user.fcmTokens || [];
  const permissions: string[] = user.permissions || [];

  const subscriptionEnd = toDate(user.subscriptionEndDate);
  const daysLeft = subscriptionEnd ? Math.ceil((subscriptionEnd.getTime() - Date.now()) / 86400000) : null;

  // ── Palette ──
  // The shared admin surfaces put dark-mode cards *darker* than the modal
  // (#111827 on #161F2E), so they sank into the background. This view uses
  // its own raised-card palette instead.
  const palette = isDarkMode
    ? {
        body: '#0A101C',
        card: '#152036',
        cardBorder: '#2A3A57',
        tabBar: '#0F1829',
        text: '#F1F5F9',
        muted: '#A3B3CC',
        track: '#243352',
        pillBg: '38',
        pillBorder: '85',
      }
    : {
        body: '#F1F5F9',
        card: '#FFFFFF',
        cardBorder: '#E2E8F0',
        tabBar: '#FFFFFF',
        text: colors.sidebarTextActive,
        muted: colors.adminMuted,
        track: '#E2E8F0',
        pillBg: '1A',
        pillBorder: '55',
      };

  // ── Small building blocks ──
  const muted = palette.muted;
  const text = palette.text;

  const Pill = ({ label, color, icon }: { label: string; color: string; icon?: string }) => (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: `${color}${palette.pillBg}`,
        borderColor: `${color}${palette.pillBorder}`,
        borderWidth: 1,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 999,
        alignSelf: 'flex-start',
      }}
    >
      {icon && <Icon name={icon as any} size={12} color={color} />}
      <Text style={{ fontSize: 11, fontFamily: fonts.semiBold, fontWeight: '600', color }}>{label}</Text>
    </View>
  );

  const Card = ({
    title,
    icon,
    children,
    full,
    accent = colors.sidebarAccent,
  }: {
    title: string;
    icon: string;
    children: React.ReactNode;
    full?: boolean;
    accent?: string;
  }) => (
    <View
      style={{
        flexGrow: 1,
        flexBasis: full || isMobile ? '100%' : 340,
        backgroundColor: palette.card,
        borderWidth: 1,
        borderColor: palette.cardBorder,
        borderTopWidth: 3,
        borderTopColor: accent,
        borderRadius: 12,
        padding: 16,
        gap: 12,
        ...(Platform.OS === 'web'
          ? { boxShadow: isDarkMode ? '0 4px 18px rgba(0,0,0,0.45)' : '0 2px 10px rgba(15,23,42,0.06)' }
          : {}),
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
        <View
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            backgroundColor: `${accent}${isDarkMode ? '30' : '1A'}`,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name={icon as any} size={16} color={accent} />
        </View>
        <Text
          style={{
            fontSize: 12,
            fontFamily: fonts.bold,
            fontWeight: '700',
            color: text,
            textTransform: 'uppercase',
            letterSpacing: 0.8,
          }}
        >
          {title}
        </Text>
      </View>
      {children}
    </View>
  );

  const Row = ({ label, value, hint, color }: { label: string; value: React.ReactNode; hint?: string; color?: string }) => (
    <View style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
      <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: muted, flexShrink: 0 }}>{label}</Text>
      <View style={{ flexShrink: 1, alignItems: 'flex-end' }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Text style={{ fontSize: 13, fontFamily: fonts.regular, color: color || text, textAlign: 'right' }}>
            {value}
          </Text>
        ) : (
          value
        )}
        {!!hint && <Text style={{ fontSize: 11, fontFamily: fonts.regular, color: muted }}>{hint}</Text>}
      </View>
    </View>
  );

  const YesNo = ({ value }: { value: boolean | undefined }) =>
    value === undefined ? (
      <Text style={{ fontSize: 13, color: muted }}>—</Text>
    ) : (
      <Pill label={value ? 'On' : 'Off'} color={value ? '#10B981' : '#94A3B8'} />
    );

  const Chips = ({ label, items, color }: { label: string; items: string[]; color: string }) =>
    items.length === 0 ? null : (
      <View style={{ gap: 6 }}>
        <Text style={{ fontSize: 11, fontFamily: fonts.medium, color: muted }}>
          {label} ({items.length})
        </Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {items.map((item, i) => (
            <Pill key={`${item}-${i}`} label={item} color={color} />
          ))}
        </View>
      </View>
    );

  const LinkRow = ({ label, url }: { label: string; url: string }) => (
    <Row
      label={label}
      value={
        <TouchableOpacity onPress={() => Linking.openURL(url.startsWith('http') ? url : `https://${url}`)}>
          <Text style={{ fontSize: 13, color: '#6366F1', textDecorationLine: 'underline' }} numberOfLines={1}>
            {url.replace(/^https?:\/\//, '').slice(0, 48)}
            {url.replace(/^https?:\/\//, '').length > 48 ? '…' : ''}
          </Text>
        </TouchableOpacity>
      }
    />
  );

  const Empty = ({ label }: { label: string }) => (
    <Text style={{ fontSize: 13, fontFamily: fonts.regular, color: muted, fontStyle: 'italic' }}>{label}</Text>
  );

  const AuthPending = () =>
    detailsError ? (
      <Text style={{ fontSize: 12, color: '#EF4444' }}>{detailsError}</Text>
    ) : (
      <ActivityIndicator size="small" color={colors.sidebarAccent} style={{ alignSelf: 'flex-start' }} />
    );

  const copyUid = () => {
    if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(user.id);
    } else {
      Clipboard.setString(user.id);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // ── Header stats ──
  const stats: { label: string; value: React.ReactNode; icon: string; color: string; hint?: string }[] = [
    {
      label: 'Applications',
      value: details ? details.stats.applications : '…',
      icon: 'work-outline',
      color: '#6366F1',
    },
    { label: 'Saved Jobs', value: savedJobsCount, icon: 'bookmark-border', color: '#F59E0B' },
    {
      label: 'Notifications',
      value: details ? details.stats.notifications : '…',
      icon: 'notifications-none',
      color: '#3B82F6',
      hint: details ? `${details.stats.unreadNotifications} unread` : undefined,
    },
    { label: 'Devices', value: devices.length, icon: 'smartphone', color: '#10B981' },
    {
      label: 'Support Tickets',
      value: details ? details.stats.supportTickets : '…',
      icon: 'support-agent',
      color: '#EC4899',
    },
  ];

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'dashboard' },
    { key: 'profile', label: 'Profile & Career', icon: 'badge' },
    ...(isCompany ? [{ key: 'company' as TabKey, label: 'Company', icon: 'business' }] : []),
    { key: 'activity', label: 'Activity', icon: 'timeline' },
    { key: 'settings', label: 'Settings', icon: 'tune' },
  ];

  // ── Tabs ──
  const renderOverview = () => (
    <>
      <Card accent="#38BDF8" title="Sign-in Methods" icon="login">
        {details ? (
          details.auth ? (
            details.auth.providers.length ? (
              details.auth.providers.map((p) => {
                const meta = PROVIDERS[p.providerId] || { label: p.providerId, icon: 'vpn-key', color: '#64748B' };
                return (
                  <View key={p.providerId} style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <View
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        backgroundColor: `${meta.color}22`,
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name={meta.icon as any} size={18} color={meta.color} />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, fontWeight: '600', color: text }}>
                        {meta.label}
                      </Text>
                      {!!(p.email || p.displayName) && (
                        <Text style={{ fontSize: 11, color: muted }} numberOfLines={1}>
                          {p.email || p.displayName}
                        </Text>
                      )}
                    </View>
                  </View>
                );
              })
            ) : (
              <Empty label="No linked providers" />
            )
          ) : (
            <Empty label="No Firebase Auth account found for this user" />
          )
        ) : (
          <AuthPending />
        )}
      </Card>

      <Card accent="#FF8C42" title="Account" icon="manage-accounts">
        <Row label="User ID" value={
          <TouchableOpacity onPress={copyUid} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <Text style={{ fontSize: 12, color: text, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}>
              {user.id.slice(0, 14)}…
            </Text>
            <Icon name={copied ? 'check' : 'content-copy'} size={13} color={copied ? '#10B981' : muted} />
          </TouchableOpacity>
        } />
        <Row label="Joined" value={formatDate(user.createdAt)} hint={timeAgo(user.createdAt)} />
        <Row
          label="Last sign-in"
          value={details?.auth ? formatDate(details.auth.lastSignInTime, true) : formatDate(user.lastLogin, true)}
          hint={timeAgo(details?.auth?.lastSignInTime || user.lastLogin)}
        />
        {details?.auth?.lastRefreshTime && (
          <Row
            label="Last active"
            value={formatDate(details.auth.lastRefreshTime, true)}
            hint={timeAgo(details.auth.lastRefreshTime)}
          />
        )}
        {user.updatedAt && <Row label="Profile updated" value={formatDate(user.updatedAt)} />}
        {(() => {
          // Firebase Auth is the source of truth; the profile's isVerified copy can be stale
          const verified = details?.auth ? details.auth.emailVerified || !!user.isVerified : !!user.isVerified;
          return (
            <Row
              label="Email verified"
              value={<Pill label={verified ? 'Verified' : 'Not verified'} color={verified ? '#10B981' : '#EF4444'} />}
            />
          );
        })()}
        <Row
          label="Profile setup"
          value={
            <Pill
              label={user.profileSetupComplete ? 'Complete' : 'Incomplete'}
              color={user.profileSetupComplete ? '#10B981' : '#F59E0B'}
            />
          }
        />
        {details?.auth?.disabled && (
          <Row label="Auth account" value={<Pill label="Disabled in Firebase Auth" color="#EF4444" />} />
        )}
      </Card>

      <Card accent="#A78BFA" title="Subscription" icon="workspace-premium">
        <Row label="Plan" value={<Pill label={plan.toUpperCase()} color={PLAN_COLORS[plan] || '#64748B'} />} />
        <Row label="Status" value={user.subscriptionStatus ? titleCase(user.subscriptionStatus) : '—'} />
        {user.subscriptionStartDate && <Row label="Started" value={formatDate(user.subscriptionStartDate)} />}
        {user.subscriptionEndDate && (
          <Row
            label="Ends"
            value={formatDate(user.subscriptionEndDate)}
            hint={daysLeft === null ? undefined : daysLeft >= 0 ? `${daysLeft} days left` : `expired ${-daysLeft} days ago`}
          />
        )}
      </Card>

      {permissions.length > 0 && (
        <Card accent="#6366F1" title="Permissions & Access" icon="admin-panel-settings">
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {permissions.map((p) => (
              <Pill key={p} label={titleCase(p)} color="#6366F1" />
            ))}
          </View>
        </Card>
      )}
    </>
  );

  const renderProfile = () => (
    <>
      <Card accent="#38BDF8" title="Personal & Contact" icon="person-outline">
        <Row label="Full name" value={name} />
        {user.username && <Row label="Username" value={`@${user.username}`} />}
        <Row label="Email" value={user.email || '—'} />
        <Row label="Phone" value={phone || '—'} />
        {user.dateOfBirth && (
          <Row label="Date of birth" value={formatDate(user.dateOfBirth)} hint={age !== null ? `${age} years old` : undefined} />
        )}
        {user.language && <Row label="Language" value={String(user.language).toUpperCase()} />}
        {!!bio && (
          <View style={{ gap: 4 }}>
            <Text style={{ fontSize: 12, fontFamily: fonts.medium, color: muted }}>Bio</Text>
            <Text style={{ fontSize: 13, color: text, lineHeight: 19 }}>{bio}</Text>
          </View>
        )}
        {socialLinks.map(([platform, url]) => (
          <LinkRow key={platform} label={titleCase(platform)} url={url} />
        ))}
        {user.bannerImage && <LinkRow label="Banner image" url={user.bannerImage} />}
      </Card>

      <Card accent="#10B981" title="Career" icon="work-outline">
        {!jobTitle && !yearsOfExperience && !education && skills.length === 0 && portfolioLinks.length === 0 ? (
          <Empty label="No career details added yet" />
        ) : (
          <>
            {jobTitle && <Row label="Job title" value={jobTitle} />}
            {user.portfolio?.company && <Row label="Current/last company" value={user.portfolio.company} />}
            {yearsOfExperience !== undefined && yearsOfExperience !== '' && (
              <Row label="Experience" value={`${yearsOfExperience} years`} />
            )}
            {!!education && <Row label="Education" value={education} />}
            {user.profileCompleteness !== undefined && (
              <Row label="Profile completeness" value={`${user.profileCompleteness}%`} />
            )}
            {portfolioLinks.map(([label, url]) => (
              <LinkRow key={label} label={label} url={url} />
            ))}
            {user.portfolio?.cvUpdatedAt && <Row label="CV updated" value={formatDate(user.portfolio.cvUpdatedAt)} />}
            <Chips label="Skills" items={skills} color="#10B981" />
            <Chips label="Skill categories" items={skillCategories} color="#0EA5E9" />
            <Chips label="Skill subcategories" items={skillSubcategories} color="#14B8A6" />
          </>
        )}
      </Card>

      <Card accent="#EC4899" title="Job Interests" icon="interests" full>
        {interestedCategories.length + interestedSubcategories.length + interestedTags.length +
          preferredCategories.length + preferredLocations.length === 0 ? (
          <Empty label="No job interests selected" />
        ) : (
          <>
            <Chips label="Categories" items={interestedCategories} color="#8B5CF6" />
            <Chips label="Subcategories" items={interestedSubcategories} color="#EC4899" />
            <Chips label="Tags" items={interestedTags} color="#3B82F6" />
            <Chips label="Preferred categories" items={preferredCategories} color="#8B5CF6" />
            <Chips label="Preferred locations" items={preferredLocations} color="#EC4899" />
          </>
        )}
      </Card>
    </>
  );

  const renderCompany = () => {
    const status = companyProfile.verificationStatus;
    return (
      <>
        <Card accent="#3B82F6" title="Company" icon="business">
          <Row label="Name" value={companyProfile.companyName || '—'} />
          <Row
            label="Verification"
            value={
              <Pill
                label={status === 'approved' ? 'Verified' : status === 'rejected' ? 'Rejected' : 'Pending'}
                color={status === 'approved' ? '#10B981' : status === 'rejected' ? '#EF4444' : '#F59E0B'}
              />
            }
          />
          {companyProfile.industry && <Row label="Industry" value={companyProfile.industry} />}
          {company.size && <Row label="Size" value={company.size} />}
          {companyProfile.foundedYear && <Row label="Founded" value={String(companyProfile.foundedYear)} />}
          {companyProfile.submittedAt && <Row label="Submitted" value={formatDate(companyProfile.submittedAt)} />}
          {companyProfile.verifiedAt && <Row label="Verified at" value={formatDate(companyProfile.verifiedAt)} />}
          {companyProfile.rejectionReason && (
            <Row label="Rejection reason" value={companyProfile.rejectionReason} color="#EF4444" />
          )}
        </Card>
        <Card accent="#06B6D4" title="Company Contact" icon="contact-mail">
          {company.email && <Row label="Email" value={company.email} />}
          {company.phone && <Row label="Phone" value={company.phone} />}
          {(companyProfile.address || companyProfile.city || companyProfile.country) && (
            <Row
              label="Address"
              value={[companyProfile.address, companyProfile.city, companyProfile.country].filter(Boolean).join(', ')}
            />
          )}
          {company.links.map(([label, url]) => (
            <LinkRow key={label} label={label} url={url} />
          ))}
        </Card>
        {company.description && (
          <Card accent="#94A3B8" title="About" icon="description" full>
            <Text style={{ fontSize: 13, color: text, lineHeight: 19 }}>{company.description}</Text>
          </Card>
        )}
      </>
    );
  };

  const renderActivity = () => (
    <>
      <Card accent="#6366F1" title="Applications by Status" icon="donut-large">
        {!details ? (
          <AuthPending />
        ) : details.stats.applications === 0 ? (
          <Empty label="Hasn't applied to any jobs yet" />
        ) : (
          <View style={{ gap: 8 }}>
            {Object.entries(details.stats.applicationsByStatus)
              .sort((a, b) => b[1] - a[1])
              .map(([status, count]) => {
                const color = APPLICATION_STATUS_COLORS[status] || '#64748B';
                const pct = Math.round((count / details.stats.applications) * 100);
                return (
                  <View key={status} style={{ gap: 4 }}>
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <Text style={{ fontSize: 12, color: text, fontFamily: fonts.medium }}>{titleCase(status)}</Text>
                      <Text style={{ fontSize: 12, color: muted }}>
                        {count} · {pct}%
                      </Text>
                    </View>
                    <View style={{ height: 6, borderRadius: 3, backgroundColor: palette.track }}>
                      <View style={{ width: `${pct}%`, height: 6, borderRadius: 3, backgroundColor: color }} />
                    </View>
                  </View>
                );
              })}
          </View>
        )}
      </Card>

      <Card accent="#F59E0B" title="Engagement" icon="insights">
        <Row label="Saved jobs" value={String(savedJobsCount)} />
        <Row
          label="Notifications received"
          value={details ? String(details.stats.notifications) : '…'}
          hint={details ? `${details.stats.unreadNotifications} unread` : undefined}
        />
        <Row label="Support tickets" value={details ? String(details.stats.supportTickets) : '…'} />
        <Row label="Registered devices" value={String(devices.length)} />
      </Card>

      <Card accent="#10B981" title="Recent Applications" icon="history" full>
        {!details ? (
          <AuthPending />
        ) : details.recentApplications.length === 0 ? (
          <Empty label="No applications" />
        ) : (
          details.recentApplications.map((a, i) => (
            <View
              key={a.id}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                paddingTop: i === 0 ? 0 : 10,
                borderTopWidth: i === 0 ? 0 : 1,
                borderTopColor: palette.cardBorder,
              }}
            >
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, fontFamily: fonts.semiBold, fontWeight: '600', color: text }} numberOfLines={1}>
                  {a.jobTitle || 'Untitled job'}
                </Text>
                <Text style={{ fontSize: 11, color: muted }} numberOfLines={1}>
                  {[a.companyName, a.appliedAt && `${formatDate(a.appliedAt)} (${timeAgo(a.appliedAt)})`]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </View>
              <Pill label={titleCase(a.status)} color={APPLICATION_STATUS_COLORS[a.status] || '#64748B'} />
            </View>
          ))
        )}
      </Card>
    </>
  );

  const renderSettings = () => (
    <>
      <Card accent="#F59E0B" title="Notification Preferences" icon="notifications-active">
        <Row label="All notifications" value={<YesNo value={user.isNotificationsEnabled} />} />
        <Row
          label="Push"
          value={<YesNo value={user.isPushNotificationsEnabled ?? user.pushNotifications} />}
        />
        <Row
          label="Email"
          value={<YesNo value={user.isEmailNotificationsEnabled ?? user.emailNotifications} />}
        />
        <Row label="Job alerts" value={<YesNo value={user.jobAlerts ?? user.notificationPreferences?.newJobs} />} />
        <Row
          label="Application updates"
          value={<YesNo value={user.applicationUpdates ?? user.notificationPreferences?.applicationUpdates} />}
        />
      </Card>

      <Card accent="#EF4444" title="Privacy" icon="privacy-tip">
        <Row
          label="Profile visibility"
          value={user.profileVisibility !== undefined ? titleCase(String(user.profileVisibility)) : '—'}
        />
        <Row label="Show email publicly" value={<YesNo value={user.showEmail} />} />
        <Row label="Allow messaging" value={<YesNo value={user.allowMessaging} />} />
      </Card>

      <Card accent="#10B981" title={`Push Devices (${devices.length})`} icon="devices" full>
        {devices.length === 0 ? (
          <Empty label="No devices registered — this user can't receive push notifications" />
        ) : (
          devices.map((token, i) => (
            <Text
              key={i}
              style={{ fontSize: 12, color: muted, fontFamily: Platform.OS === 'web' ? 'monospace' : undefined }}
              numberOfLines={1}
            >
              Device {i + 1}: {token.substring(0, 48)}…
            </Text>
          ))
        )}
      </Card>
    </>
  );

  const content: Record<TabKey, () => React.ReactNode> = {
    overview: renderOverview,
    profile: renderProfile,
    company: renderCompany,
    activity: renderActivity,
    settings: renderSettings,
  };

  const roleColor = ROLE_COLORS[role] || ROLE_COLORS.user;
  const signInLabels = details?.auth?.providers.map((p) => PROVIDERS[p.providerId]?.label || p.providerId) || [];

  return (
    <View
      style={{
        position: 'fixed' as any,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: isDarkMode ? 'rgba(2, 6, 16, 0.78)' : 'rgba(15, 23, 42, 0.6)',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      {/* Backdrop click closes */}
      <TouchableOpacity
        activeOpacity={1}
        onPress={onClose}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      />
      <View
        style={{
          backgroundColor: palette.body,
          borderRadius: isMobile ? 0 : 16,
          width: isMobile ? '100%' : '94%',
          maxWidth: 1040,
          height: isMobile ? ('100%' as any) : ('90vh' as any),
          overflow: 'hidden',
          ...(Platform.OS === 'web'
            ? { boxShadow: isDarkMode ? '0 20px 60px rgba(0,0,0,0.6)' : '0 20px 60px rgba(15,23,42,0.25)' }
            : {}),
        }}
      >
        {/* ── Hero ── */}
        <View
          style={{
            backgroundColor: isDarkMode ? '#17243D' : colors.adminHeaderBg,
            borderBottomWidth: 3,
            borderBottomColor: roleColor,
            paddingHorizontal: isMobile ? 16 : 24,
            paddingTop: 20,
            paddingBottom: 16,
          }}
        >
          <View style={{ flexDirection: 'row', gap: 16, alignItems: 'center' }}>
            {avatar ? (
              <TouchableOpacity onPress={() => setPhotoOpen(true)} activeOpacity={0.85}>
                <Image
                  source={{ uri: avatar }}
                  onError={() => setAvatarFailed(true)}
                  style={{
                    width: isMobile ? 56 : 72,
                    height: isMobile ? 56 : 72,
                    borderRadius: 999,
                    borderWidth: 3,
                    borderColor: roleColor,
                  }}
                />
                <View
                  style={{
                    position: 'absolute',
                    right: -2,
                    bottom: -2,
                    width: 22,
                    height: 22,
                    borderRadius: 11,
                    backgroundColor: roleColor,
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderWidth: 2,
                    borderColor: isDarkMode ? '#17243D' : colors.adminHeaderBg,
                  }}
                >
                  <Icon name="zoom-in" size={12} color="#FFFFFF" />
                </View>
              </TouchableOpacity>
            ) : (
              <View
                style={{
                  width: isMobile ? 52 : 64,
                  height: isMobile ? 52 : 64,
                  borderRadius: 999,
                  backgroundColor: `${roleColor}33`,
                  borderWidth: 2,
                  borderColor: roleColor,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Text style={{ fontSize: 24, fontFamily: fonts.bold, fontWeight: '700', color: roleColor }}>
                  {(name !== 'No Name' ? name : user.email || '?').charAt(0).toUpperCase()}
                </Text>
              </View>
            )}
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: isMobile ? 17 : 20, fontFamily: fonts.bold, fontWeight: '700', color: '#F9FAFB' }} numberOfLines={1}>
                {name}
              </Text>
              <Text style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }} numberOfLines={1}>
                {user.email}
                {user.username ? `  ·  @${user.username}` : ''}
              </Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                <Pill label={role.toUpperCase()} color={roleColor} icon="shield" />
                <Pill
                  label={user.isActive === false ? 'INACTIVE' : 'ACTIVE'}
                  color={user.isActive === false ? '#EF4444' : '#10B981'}
                  icon={user.isActive === false ? 'block' : 'check-circle'}
                />
                <Pill label={plan.toUpperCase()} color={PLAN_COLORS[plan] || '#64748B'} icon="workspace-premium" />
                {signInLabels.length > 0 && <Pill label={signInLabels.join(' + ')} color="#38BDF8" icon="login" />}
              </View>
            </View>
            <TouchableOpacity
              onPress={onClose}
              style={{
                alignSelf: 'flex-start',
                width: 34,
                height: 34,
                borderRadius: 9,
                backgroundColor: '#FFFFFF18',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="close" size={18} color="#CBD5E1" />
            </TouchableOpacity>
          </View>

          {/* Stat strip */}
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 18 }}>
            {stats.map((s) => (
              <View
                key={s.label}
                style={{
                  flexGrow: 1,
                  flexBasis: isMobile ? '30%' : 0,
                  backgroundColor: isDarkMode ? '#FFFFFF12' : '#FFFFFF0D',
                  borderWidth: 1,
                  borderColor: '#FFFFFF14',
                  borderRadius: 10,
                  paddingVertical: 10,
                  paddingHorizontal: 12,
                  borderLeftWidth: 3,
                  borderLeftColor: s.color,
                }}
              >
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                  <Icon name={s.icon as any} size={13} color={s.color} />
                  <Text style={{ fontSize: 10, color: '#94A3B8', fontFamily: fonts.medium }} numberOfLines={1}>
                    {s.label}
                  </Text>
                </View>
                <Text style={{ fontSize: 20, color: '#F9FAFB', fontFamily: fonts.bold, fontWeight: '700', marginTop: 2 }}>
                  {s.value}
                </Text>
                {s.hint && <Text style={{ fontSize: 10, color: '#94A3B8' }}>{s.hint}</Text>}
              </View>
            ))}
          </View>
        </View>

        {/* ── Tabs ── */}
        <View style={{ borderBottomWidth: 1, borderBottomColor: palette.cardBorder, backgroundColor: palette.tabBar }}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: isMobile ? 8 : 16 }}>
            {tabs.map((t) => {
              const active = tab === t.key;
              return (
                <TouchableOpacity
                  key={t.key}
                  onPress={() => setTab(t.key)}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                    paddingHorizontal: 14,
                    paddingVertical: 12,
                    borderBottomWidth: 2,
                    borderBottomColor: active ? colors.sidebarAccent : 'transparent',
                  }}
                >
                  <Icon name={t.icon as any} size={16} color={active ? colors.sidebarAccent : muted} />
                  <Text
                    style={{
                      fontSize: 13,
                      fontFamily: active ? fonts.semiBold : fonts.medium,
                      fontWeight: active ? '600' : '500',
                      color: active ? text : muted,
                    }}
                  >
                    {t.label}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        </View>

        {/* ── Body ── */}
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 12 : 20 }}>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 14 }}>{content[tab]()}</View>
        </ScrollView>
      </View>

      {/* ── Full-size photo viewer ── */}
      {photoOpen && fullAvatar && (
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(2, 6, 16, 0.92)',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <TouchableOpacity
            activeOpacity={1}
            onPress={() => setPhotoOpen(false)}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          />
          <Image
            source={{ uri: fullAvatar }}
            resizeMode="contain"
            style={{ width: isMobile ? '92%' : ('min(80vw, 80vh)' as any), height: isMobile ? '70%' : ('min(80vw, 80vh)' as any), borderRadius: 12 }}
          />
          <Text style={{ color: '#E2E8F0', fontSize: 14, fontFamily: fonts.semiBold, fontWeight: '600', marginTop: 14 }}>
            {name}
          </Text>
          <View style={{ position: 'absolute', top: 16, right: 16, flexDirection: 'row', gap: 8 }}>
            {!fullAvatar.startsWith('data:') && (
              <TouchableOpacity
                onPress={() => Linking.openURL(fullAvatar)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  paddingHorizontal: 12,
                  height: 38,
                  borderRadius: 10,
                  backgroundColor: '#FFFFFF1F',
                }}
              >
                <Icon name="open-in-new" size={16} color="#F1F5F9" />
                <Text style={{ color: '#F1F5F9', fontSize: 13, fontFamily: fonts.medium }}>Open original</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity
              onPress={() => setPhotoOpen(false)}
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                backgroundColor: '#FFFFFF1F',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon name="close" size={20} color="#F1F5F9" />
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}
